/**
 * VaultSidebar - Left pane for Smart Sets and Boxes
 * USER-CON-001 Phase 2
 */
import React, { useState, useEffect } from 'react';
import {
  FolderOpen, Box, Star, DollarSign, Heart, Copy, Award, Pen,
  ChevronDown, ChevronRight, Plus, Settings, RefreshCw
} from 'lucide-react';
import { getSmartSets, getBoxes, computeSmartSets } from '../../services/vaultApi';
import CompletionBar from './CompletionBar';
import QuotaMeter from './QuotaMeter';
import useQuota from '../../hooks/useQuota';
// V-NAV-RAIL: the Supply Lab trigger below is gated on the same flag as the routes it targets.
import { FEATURES } from '../../features';

// Icon mapping for smart sets
const SMART_SET_ICONS = {
  trophy: Award,
  pen: Pen,
  heart: Heart,
  'dollar-sign': DollarSign,
  copy: Copy,
  star: Star,
};

function SmartSetItem({ set, isActive, onClick }) {
  const IconComponent = SMART_SET_ICONS[set.icon] || FolderOpen;

  return (
    <button
      onClick={() => onClick(set)}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left ${
        isActive
          ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
          : 'hover:bg-zinc-800 text-zinc-300'
      }`}
    >
      <div
        className="w-5 h-5 rounded flex items-center justify-center"
        style={{ backgroundColor: set.color + '20' }}
      >
        <IconComponent
          className="w-3 h-3"
          style={{ color: set.color }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="truncate text-sm">{set.name}</span>
          <span className="text-xs text-zinc-500 ml-1 flex-shrink-0">{set.cached_count || 0}</span>
        </div>
        {set.total_in_set > 0 && (
          <CompletionBar
            percentage={set.completion_pct || 0}
            size="sm"
          />
        )}
      </div>
    </button>
  );
}

function BoxItem({ box, isActive, onClick }) {
  const fillPct = box.fill_percentage || 0;
  const fillColor = fillPct > 90 ? 'bg-red-500' : fillPct > 70 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <button
      onClick={() => onClick(box)}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left ${
        isActive
          ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
          : 'hover:bg-zinc-800 text-zinc-300'
      }`}
    >
      <Box className="w-4 h-4 text-zinc-500" />
      <div className="flex-1 min-w-0">
        <span className="block truncate text-sm">{box.name}</span>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1 bg-zinc-700 rounded-full overflow-hidden">
            <div
              className={`h-full ${fillColor} transition-all`}
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <span className="text-[10px] text-zinc-500">{box.item_count}/{box.capacity}</span>
        </div>
      </div>
    </button>
  );
}

export default function VaultSidebar({
  activeView,
  activeSmartSet,
  activeBox,
  onViewChange,
  onSmartSetSelect,
  onBoxSelect,
  onCreateBox,
  onCreateSmartSet,
}) {
  const [smartSets, setSmartSets] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { quota } = useQuota();
  const [expandedSections, setExpandedSections] = useState({
    smartSets: true,
    boxes: true,
  });

  useEffect(() => {
    loadSidebarData();
  }, []);

  const loadSidebarData = async () => {
    setIsLoading(true);
    try {
      const [setsResult, boxesResult] = await Promise.all([
        getSmartSets(),
        getBoxes(),
      ]);
      setSmartSets(setsResult.items || []);
      setBoxes(boxesResult.items || []);
    } catch (error) {
      console.error('Failed to load sidebar data:', error);
    }
    setIsLoading(false);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Separate system and user smart sets
  const systemSets = smartSets.filter(s => s.is_system);
  const userSets = smartSets.filter(s => !s.is_system);

  return (
    <div className="w-64 bg-zinc-900 border-r border-zinc-800 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <button
          onClick={() => onViewChange('all')}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            activeView === 'all'
              ? 'bg-orange-500 text-white'
              : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
          }`}
        >
          <FolderOpen className="w-4 h-4" />
          <span className="font-medium">All Items</span>
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Smart Sets Section */}
        <div>
          <button
            onClick={() => toggleSection('smartSets')}
            className="flex items-center justify-between w-full text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2"
          >
            <span>Smart Sets</span>
            {expandedSections.smartSets ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {expandedSections.smartSets && (
            <div className="space-y-1">
              {/* System Sets */}
              {systemSets.map(set => (
                <SmartSetItem
                  key={set.id}
                  set={set}
                  isActive={activeSmartSet?.id === set.id}
                  onClick={onSmartSetSelect}
                />
              ))}

              {/* Divider if both system and user sets exist */}
              {systemSets.length > 0 && userSets.length > 0 && (
                <div className="border-t border-zinc-800 my-2" />
              )}

              {/* User Sets */}
              {userSets.map(set => (
                <SmartSetItem
                  key={set.id}
                  set={set}
                  isActive={activeSmartSet?.id === set.id}
                  onClick={onSmartSetSelect}
                />
              ))}

              {/* Create button */}
              <button
                onClick={onCreateSmartSet}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <Plus className="w-3 h-3" />
                <span>New Smart Set</span>
              </button>
            </div>
          )}
        </div>

        {/* Boxes Section */}
        <div>
          <button
            onClick={() => toggleSection('boxes')}
            className="flex items-center justify-between w-full text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2"
          >
            <span>Boxes</span>
            {expandedSections.boxes ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {expandedSections.boxes && (
            <div className="space-y-1">
              {boxes.map(box => (
                <BoxItem
                  key={box.id}
                  box={box}
                  isActive={activeBox?.id === box.id}
                  onClick={onBoxSelect}
                />
              ))}

              {boxes.length === 0 && (
                <p className="text-xs text-zinc-500 px-3 py-2">
                  No boxes yet. Create one to organize your collection.
                </p>
              )}

              {/* Create button */}
              <button
                onClick={onCreateBox}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <Plus className="w-3 h-3" />
                <span>New Box</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* V-NAV-RAIL: the Vault is the authenticated HOME and its header back arrow is gone
          (V-NAV-EGRESS), so the beta shop needs a way OUT of the overlay. The store user-menu
          sits underneath the fixed overlay and cannot be reached from here. A plain anchor on
          purpose -- VaultSidebar is rendered with no Router in its suite and in VaultPage, so
          no useNavigate/<Link>; the browser navigation is what a shop hand-off should be. */}
      {FEATURES.SUPPLIES_ENABLED && (
        <div className="border-t border-zinc-800 px-3 py-2">
          <a
            href="/maestro/supply-lab"
            aria-label="Supply Lab"
            data-testid="vault-rail-supply-lab"
            className="flex items-center gap-3 px-3 rounded-lg min-h-touch text-zinc-300 hover:text-maestro-gold hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-maestro-gold"
          >
            {/* The MaestroNav wordmark motif: gold monospace M. */}
            <span
              aria-hidden="true"
              className="font-maestro-mono text-base font-bold tracking-widest text-maestro-gold"
              style={{ letterSpacing: '0.15em' }}
            >
              M
            </span>
            <span className="text-sm font-medium">Supply Lab</span>
          </a>
        </div>
      )}

      {/* Quota Meter */}
      <div className="border-t border-zinc-800">
        <QuotaMeter quota={quota} />
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-zinc-800">
        <button
          onClick={loadSidebarData}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>
    </div>
  );
}
